import React, { useEffect, useMemo, useState } from 'react';
import {
    Button, Flex, Select, Table, Typography, Modal, Tag, Input, Empty,
    message, Image, Space, Row, Col, Switch, Tooltip, Segmented, Badge,
} from 'antd';
import { FaEdit, FaTrashAlt } from 'react-icons/fa';
import { useRecoilValue } from 'recoil';
import { themeState } from '../../atom';
import CustomCard from '../../components/Card';
import ApiService from '../../APIServices/ApiService';
import OtherBookForm from './OtherBookForm';

/**
 * Admin catalogue for the four sister storefronts, served by /api/other-books.
 *
 * Separate from the travel-guide Books page rather than a mode of it: these
 * books have no city/lat/long, their `category` is a site key instead of a
 * Category document, and they carry an EPUB. Folding both into one screen would
 * have meant a form where half the fields are conditionally irrelevant.
 *
 * THE LAYOUT IS BUILT AROUND ONE FACT: this is a ~5,000-book catalogue spread
 * over four businesses, and almost every job on it starts by narrowing to one
 * of them. So the storefront cards are the primary filter rather than a row of
 * read-only numbers above an unrelated dropdown, every other control sits in
 * one toolbar, and what is currently filtered is stated in words with a way to
 * undo it. Nothing about what the page DOES changed here.
 */

/** Fallback accents, in registry order, if /sites ever stops sending one. */
const FALLBACK_ACCENTS = ['#2f7a4f', '#1f3a93', '#14607a', '#6b2737'];

const OtherBooks = () => {
    const theme = useRecoilValue(themeState);
    const dark = theme === 'dark';

    const [sites, setSites] = useState([]);
    const [stats, setStats] = useState([]);
    const [books, setBooks] = useState([]);
    const [tableLoading, setTableLoading] = useState(false);

    const [site, setSite] = useState('');       // '' = every storefront
    const [status, setStatus] = useState('');   // '' = published AND draft
    const [query, setQuery] = useState('');
    const [search, setSearch] = useState('');   // what is typed, before Enter

    /*
     * The SUBJECT categories -- what the books are about -- as opposed to `site`
     * above, which is the storefront that sells them. One word apart, completely
     * different fields: `category` on the API means the storefront, the subject
     * is `subjectCategory`.
     *
     * These are derived from each title by the backend and stored on the book, so
     * this rail is counted from real data rather than assembled here.
     */
    const [categories, setCategories] = useState([]);
    const [uncategorised, setUncategorised] = useState(0);
    const [subject, setSubject] = useState('');  // '' = every subject
    const [allSubjects, setAllSubjects] = useState(false);

    const [formVisible, setFormVisible] = useState(false);
    const [editing, setEditing] = useState(null);

    const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 });

    useEffect(() => {
        ApiService.getOtherBookSites()
            .then((r) => setSites(r?.sites || []))
            .catch(() => message.error('Could not load the storefront list.'));
        loadStats();
    }, []);

    useEffect(() => {
        fetchBooks(1, query);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [site, status, subject]);

    // Re-counted whenever the storefront changes: the rail must describe the
    // catalogue actually on screen, not the union of all four brands.
    useEffect(() => {
        loadCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [site]);

    // A subject that exists on one storefront may not exist on another, and a
    // filter pinned to a category with nothing in it looks like an empty
    // catalogue. Clear it when the storefront changes.
    useEffect(() => {
        setSubject('');
    }, [site]);

    const loadCategories = () => {
        const params = {};
        if (site) params.category = site;
        ApiService.getOtherBookCategories(params)
            .then((r) => {
                setCategories(r?.categories || []);
                setUncategorised(r?.uncategorised || 0);
            })
            .catch(() => {
                // The rail is an aid, not the page. Losing it must not stop an
                // editor finding a book by search.
                setCategories([]);
                setUncategorised(0);
            });
    };

    const loadStats = () => {
        ApiService.getOtherBookStats()
            .then((r) => setStats(r?.stats || []))
            .catch(() => setStats([]));
    };

    const fetchBooks = (page, q, pageSize = pagination.pageSize) => {
        setTableLoading(true);
        const params = { page, limit: pageSize };
        if (site) params.category = site;
        // NOT `category` -- that is the storefront on this API.
        if (subject) params.subjectCategory = subject;
        if (status !== '') params.status = status;
        if (q && q.trim().length >= 2) params.q = q.trim();

        ApiService.getOtherBooks(params)
            .then((r) => {
                setBooks(r?.books || []);
                setPagination((prev) => ({
                    ...prev,
                    current: r?.pagination?.page || page,
                    pageSize: r?.pagination?.limit || pageSize,
                    total: r?.pagination?.total || 0,
                }));
            })
            .catch((error) => {
                setBooks([]);
                message.error(error?.response?.data?.message || 'Failed to load books.');
            })
            .finally(() => setTableLoading(false));
    };

    const toggleStatus = async (record, next) => {
        try {
            await ApiService.setOtherBookStatus(record._id, next);
            setBooks((prev) => prev.map((b) => (b._id === record._id ? { ...b, status: next } : b)));
            message.success(next ? 'Published.' : 'Unpublished.');
            loadStats();
        } catch (error) {
            message.error(error?.response?.data?.message || 'Could not change the status.');
        }
    };

    /**
     * Deleting a sold book does not revoke access — it breaks the buyer's
     * download. The API refuses with 409 has_paid_orders; rather than surface a
     * raw error, offer the two real choices.
     */
    const remove = async (record, force = false) => {
        try {
            await ApiService.deleteOtherBook(record._id, { force });
            message.success('Book deleted.');
            fetchBooks(pagination.current, query);
            loadStats();
        } catch (error) {
            const data = error?.response?.data;
            if (data?.reason === 'has_paid_orders') {
                Modal.confirm({
                    title: 'This book has been sold',
                    content: data.message
                        || 'It appears on paid orders. Deleting it will break those downloads. Unpublishing hides it from the shop without breaking anything.',
                    okText: 'Delete anyway',
                    okButtonProps: { danger: true },
                    cancelText: 'Unpublish instead',
                    onOk: () => remove(record, true),
                    onCancel: () => toggleStatus(record, false),
                });
                return;
            }
            message.error(data?.message || 'Delete failed.');
        }
    };

    const confirmDelete = (record) => {
        Modal.confirm({
            title: 'Delete this book?',
            content: record.eng_name,
            okText: 'Delete',
            okButtonProps: { danger: true },
            cancelText: 'Cancel',
            onOk: () => remove(record),
        });
    };

    /**
     * Open a stored file in a new tab.
     *
     * The listing deliberately does not carry pdfPath/epubPath/jsonPath — those
     * are the product, and a catalogue response must never hand out the stored
     * URL. So the signed link is fetched per click from GET /admin/books/:id,
     * which is the one endpoint that exposes them.
     *
     * The tab is opened BEFORE the await and pointed afterwards: opening it in
     * the promise callback loses the user-gesture context and Safari blocks it
     * as a popup.
     */
    const openFile = async (record, kind) => {
        const tab = window.open('', '_blank');
        try {
            const r = await ApiService.getOtherBook(record._id);
            const url = r?.book?.files?.[`${kind}Preview`];
            if (!url) {
                if (tab) tab.close();
                message.warning(`This book has no ${kind.toUpperCase()} file.`);
                return;
            }
            if (tab) tab.location = url;
            else window.open(url, '_blank', 'noopener');
        } catch (error) {
            if (tab) tab.close();
            message.error(error?.response?.data?.message || `Could not open the ${kind.toUpperCase()}.`);
        }
    };

    /** Storefront key -> its brand accent and display name, from /sites. */
    const siteMeta = useMemo(() => {
        const map = {};
        (sites || []).forEach((s, i) => {
            map[s.key] = {
                name: s.name,
                accent: s.accent || FALLBACK_ACCENTS[i % FALLBACK_ACCENTS.length],
                url: s.url,
            };
        });
        return map;
    }, [sites]);

    const activeFilters = [
        site && { key: 'site', label: siteMeta[site]?.name || site, clear: () => setSite('') },
        status !== '' && {
            key: 'status',
            label: status === 'true' ? 'Published only' : 'Drafts only',
            clear: () => setStatus(''),
        },
        subject && {
            key: 'subject',
            label: (categories.find((c) => c.value === subject) || {}).label || subject,
            clear: () => setSubject(''),
        },
        query && { key: 'q', label: `“${query}”`, clear: () => { setQuery(''); setSearch(''); fetchBooks(1, ''); } },
    ].filter(Boolean);

    const clearAll = () => {
        setStatus('');
        setSubject('');
        setQuery('');
        setSearch('');
        setSite('');
        // `site` and `subject` changes refetch through their effects; a bare
        // search clear does not, so it is driven explicitly.
        fetchBooks(1, '');
    };

    const columns = [
        {
            title: 'Cover',
            dataIndex: 'imagePath',
            key: 'imagePath',
            width: 90,
            render: (imagePath, record) => {
                // `image` is the presigned thumbnail and the only one that
                // actually loads: imagePath is the raw Wasabi URL, which the
                // bucket does not serve publicly. It is kept as a last resort
                // so a misconfiguration shows a broken image rather than
                // nothing, which is the difference between "no cover uploaded"
                // and "signing is not working".
                const src = record.image || record.cover || imagePath;
                if (!src) {
                    return (
                        <div className="og-cover og-cover--empty">No cover</div>
                    );
                }
                return (
                    <Image src={src} className="og-cover" rootClassName="og-cover-root" placeholder />
                );
            },
        },
        {
            title: 'Title',
            dataIndex: 'eng_name',
            key: 'eng_name',
            // The widest column by far, so it is the one that gets the slack and
            // the one that truncates — a 60-character title used to push the
            // actions off the right-hand edge.
            render: (v, r) => (
                <div style={{ minWidth: 220, maxWidth: 460 }}>
                    <Typography.Text strong ellipsis={{ tooltip: v }} style={{ display: 'block' }}>
                        {v}
                    </Typography.Text>
                    {r.name && r.name !== v && (
                        <Typography.Text type="secondary" ellipsis={{ tooltip: r.name }} style={{ display: 'block', fontSize: 12 }}>
                            {r.name}
                        </Typography.Text>
                    )}
                    {r.slug && (
                        // Ellipsised, not wrapped: these slugs run to 60-odd
                        // characters and a wrapped one doubles the height of
                        // every row in the table.
                        <Typography.Text
                            type="secondary"
                            ellipsis={{ tooltip: `/${r.slug}` }}
                            style={{ display: 'block', fontSize: 11, opacity: 0.75 }}
                        >
                            /{r.slug}
                        </Typography.Text>
                    )}
                </div>
            ),
        },
        {
            title: 'Storefront',
            dataIndex: 'site',
            key: 'site',
            width: 140,
            render: (v, r) => {
                const key = v || r.category;
                const meta = siteMeta[key];
                return (
                    <Tooltip title="Show only this storefront">
                        <Tag
                            className="og-site-tag"
                            onClick={() => setSite(site === key ? '' : key)}
                            style={{
                                cursor: 'pointer',
                                color: meta?.accent,
                                borderColor: meta?.accent,
                                background: 'transparent',
                            }}
                        >
                            {meta ? meta.name : key}
                        </Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Country',
            dataIndex: 'country',
            key: 'country',
            width: 150,
            render: (v) => v || <Typography.Text type="secondary">—</Typography.Text>,
        },
        {
            title: 'Price',
            dataIndex: 'price',
            key: 'price',
            width: 110,
            align: 'right',
            render: (v, r) => (
                <span style={{ whiteSpace: 'nowrap' }}>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>{r.currency || ''}</Typography.Text>{' '}
                    <Typography.Text strong>{Number(v || 0).toFixed(2)}</Typography.Text>
                </span>
            ),
        },
        {
            title: 'Files',
            key: 'formats',
            width: 160,
            render: (_, r) => (
                <Space size={4}>
                    <Tooltip title="Open the PDF in a new tab">
                        <Tag color="volcano" className="og-file-tag" onClick={() => openFile(r, 'pdf')}>PDF</Tag>
                    </Tooltip>
                    {r.hasEpub ? (
                        <Tooltip title="Open the EPUB in a new tab">
                            <Tag color="geekblue" className="og-file-tag" onClick={() => openFile(r, 'epub')}>EPUB</Tag>
                        </Tooltip>
                    ) : null}
                    {r.hasJson ? (
                        <Tooltip title="Open the JSON in a new tab">
                            <Tag className="og-file-tag" onClick={() => openFile(r, 'json')}>JSON</Tag>
                        </Tooltip>
                    ) : null}
                </Space>
            ),
        },
        {
            title: 'Subject',
            dataIndex: 'subjectCategoryName',
            key: 'subjectCategory',
            width: 180,
            render: (label, r) =>
                label ? (
                    <Tooltip title="Show only this subject">
                        <Tag
                            className="og-file-tag"
                            color={subject === r.subjectCategory ? 'blue' : undefined}
                            onClick={() => setSubject(subject === r.subjectCategory ? '' : r.subjectCategory)}
                        >
                            {label}
                        </Tag>
                    </Tooltip>
                ) : (
                    <Typography.Text type="secondary">—</Typography.Text>
                ),
        },
        {
            title: 'Published',
            dataIndex: 'status',
            key: 'status',
            width: 110,
            align: 'center',
            render: (v, r) => (
                <Tooltip title={v !== false ? 'Visible in the shop' : 'Hidden from the shop'}>
                    <Switch size="small" checked={v !== false} onChange={(next) => toggleStatus(r, next)} />
                </Tooltip>
            ),
        },
        {
            title: '',
            key: 'action',
            width: 90,
            // Pinned: with nine columns the actions used to scroll off the right
            // edge, so the two things an editor came to do were the two hardest
            // to reach.
            fixed: 'right',
            align: 'center',
            render: (_, record) => (
                <Flex justify="center">
                    <Tooltip title="Edit">
                        <Button type="text" onClick={() => { setEditing(record); setFormVisible(true); }}>
                            <FaEdit />
                        </Button>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <Button type="text" danger onClick={() => confirmDelete(record)}>
                            <FaTrashAlt />
                        </Button>
                    </Tooltip>
                </Flex>
            ),
        },
    ];

    const onTableChange = (value) => {
        setPagination(value);
        fetchBooks(value.current, query, value.pageSize);
    };

    // FILTERED FIRST, THEN SLICED. The API returns the whole vocabulary --
    // every subject, including the ones holding nothing on this storefront --
    // so slicing to twelve before dropping the empties showed twelve empty
    // categories, i.e. no chips at all beside a "Show all 30 subjects" link
    // that implied there were thirty to see.
    const usedCategories = categories.filter((c) => c.total > 0);
    const visibleCategories = allSubjects ? usedCategories : usedCategories.slice(0, 12);

    return (
        <div className={`other-guides ${dark ? 'other-guides--dark' : ''}`}>
            <Flex justify="space-between" align="flex-start" wrap="wrap" gap={12} className="mb-2">
                <div>
                    <Typography.Title level={4} className="my-0 fw-500">
                        Other Guides
                    </Typography.Title>
                    <Typography.Text type="secondary">
                        One catalogue, four storefronts — YouFlora, Cybellium, Accounvia and Lawlegiance.
                    </Typography.Text>
                </div>
                <Button
                    className="custom-primary-btn"
                    type="primary"
                    size="large"
                    onClick={() => { setEditing(null); setFormVisible(true); }}
                >
                    <span>Add Book</span>
                </Button>
            </Flex>

            {/* The storefront cards ARE the storefront filter. They were four
                read-only tiles above a dropdown that did the same job, which
                meant the most useful thing on the page was not clickable. */}
            {stats && stats.length > 0 && (
                <Row gutter={[12, 12]} className="mb-2">
                    {stats.map((s) => {
                        const meta = siteMeta[s.site] || {};
                        const selected = site === s.site;
                        return (
                            <Col key={s.site} xs={24} sm={12} xl={6}>
                                <button
                                    type="button"
                                    className={`og-site-card ${selected ? 'is-selected' : ''}`}
                                    style={{ '--og-accent': meta.accent || FALLBACK_ACCENTS[0] }}
                                    onClick={() => setSite(selected ? '' : s.site)}
                                    aria-pressed={selected}
                                >
                                    <span className="og-site-card__bar" />
                                    <span className="og-site-card__body">
                                        <span className="og-site-card__head">
                                            <span className="og-site-card__name">{s.name}</span>
                                            {selected && <Tag className="og-site-card__chip">Filtering</Tag>}
                                        </span>
                                        <span className="og-site-card__count">
                                            {Number(s.booksPublished || 0).toLocaleString()}
                                            <span className="og-site-card__unit">published</span>
                                        </span>
                                        <span className="og-site-card__meta">
                                            {s.booksDraft > 0 && (
                                                <Badge
                                                    color="#faad14"
                                                    text={`${s.booksDraft.toLocaleString()} draft`}
                                                />
                                            )}
                                            <span>
                                                {Number(s.ordersPaid || 0).toLocaleString()} paid ·{' '}
                                                {s.currency} {Number(s.revenue || 0).toFixed(2)}
                                            </span>
                                        </span>
                                    </span>
                                </button>
                            </Col>
                        );
                    })}
                </Row>
            )}

            {/* Every control in one place, and every ACTIVE filter stated in
                words underneath it. On a 4,917-book catalogue, "why am I seeing
                these 50 rows" is the question the page has to answer. */}
            <CustomCard className="mb-2">
                <Flex wrap="wrap" gap={10} align="center">
                    <Input.Search
                        placeholder="Search title, slug or country"
                        allowClear
                        value={search}
                        enterButton
                        className="og-search"
                        onChange={(e) => {
                            setSearch(e.target.value);
                            // Clearing the box restores the unfiltered list at
                            // once; the API needs two characters to search, so
                            // typing is not what triggers a request.
                            if (!e.target.value && query) { setQuery(''); fetchBooks(1, ''); }
                        }}
                        onSearch={(value) => {
                            const next = (value || '').trim();
                            if (next && next.length < 2) {
                                message.info('Type at least two characters to search.');
                                return;
                            }
                            setQuery(next);
                            fetchBooks(1, next);
                        }}
                    />

                    <Segmented
                        value={status}
                        onChange={setStatus}
                        options={[
                            { label: 'All', value: '' },
                            { label: 'Published', value: 'true' },
                            { label: 'Draft', value: 'false' },
                        ]}
                    />

                    <Select
                        value={site || ''}
                        style={{ minWidth: 190 }}
                        onChange={setSite}
                        className={theme === 'light' ? 'header-search-input-light' : 'header-search-input-dark'}
                        options={[
                            { value: '', label: 'All storefronts' },
                            ...(sites || []).map((s) => ({ value: s.key, label: s.name })),
                        ]}
                    />

                    <div style={{ flex: 1 }} />

                    {uncategorised > 0 && (
                        /*
                          While this is above zero the backfill has not finished.
                          Surfaced rather than hidden: those books still answer as
                          "General & Reference" on the storefront, which looks like
                          a classification bug unless you know a backfill is owed.
                          Fix with scripts/backfill-otherbook-categories.js.
                        */
                        <Tooltip title="These books have no stored subject yet and fall back to one derived from their title. Run scripts/backfill-otherbook-categories.js to store them.">
                            <Tag color="orange" style={{ marginInlineEnd: 0 }}>
                                {uncategorised.toLocaleString()} awaiting backfill
                            </Tag>
                        </Tooltip>
                    )}
                </Flex>

                {/* The subject filter. Deliberately a rail rather than another
                    dropdown: the counts are the point — an editor wants to see
                    that Law holds 851 titles and Astronomy holds four, which a
                    closed select hides. */}
                {categories.length > 0 && (
                    <Space size={[6, 6]} wrap style={{ marginTop: 14 }}>
                        <Tag
                            color={subject === '' ? 'blue' : undefined}
                            className="og-file-tag"
                            onClick={() => setSubject('')}
                        >
                            All subjects · {categories.reduce((n, c) => n + c.total, 0).toLocaleString()}
                        </Tag>
                        {visibleCategories
                            .map((c) => (
                                <Tag
                                    key={c.value}
                                    color={subject === c.value ? 'blue' : undefined}
                                    className="og-file-tag"
                                    onClick={() => setSubject(subject === c.value ? '' : c.value)}
                                >
                                    {c.label} · {c.total.toLocaleString()}
                                    {c.draft > 0 ? ` (${c.draft} draft)` : ''}
                                </Tag>
                            ))}
                        {usedCategories.length > 12 && (
                            <Button type="link" size="small" onClick={() => setAllSubjects((v) => !v)}>
                                {allSubjects
                                    ? 'Show fewer'
                                    : `Show all ${usedCategories.length} subjects`}
                            </Button>
                        )}
                    </Space>
                )}
            </CustomCard>

            <CustomCard>
                <Flex justify="space-between" align="center" wrap="wrap" gap={8} style={{ marginBottom: 12 }}>
                    <Space size={[6, 6]} wrap>
                        <Typography.Text type="secondary">
                            {tableLoading
                                ? 'Loading…'
                                : `${pagination.total.toLocaleString()} ${pagination.total === 1 ? 'book' : 'books'}`}
                        </Typography.Text>
                        {activeFilters.map((f) => (
                            <Tag key={f.key} closable onClose={f.clear} color="blue">
                                {f.label}
                            </Tag>
                        ))}
                    </Space>
                    {activeFilters.length > 0 && (
                        <Button type="link" size="small" onClick={clearAll}>Clear filters</Button>
                    )}
                </Flex>

                <Table
                    size="middle"
                    className="custom_table og-table"
                    rowKey="_id"
                    columns={columns}
                    dataSource={books}
                    loading={tableLoading}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        pageSizeOptions: [25, 50, 100],
                        showTotal: (total, range) => `${range[0]}–${range[1]} of ${total.toLocaleString()}`,
                    }}
                    onChange={onTableChange}
                    scroll={{ x: 'max-content' }}
                    locale={{
                        emptyText: (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    activeFilters.length > 0
                                        ? 'No book matches these filters.'
                                        : 'No books yet.'
                                }
                            >
                                {activeFilters.length > 0 ? (
                                    <Button onClick={clearAll}>Clear filters</Button>
                                ) : (
                                    <Button type="primary" onClick={() => { setEditing(null); setFormVisible(true); }}>
                                        Add the first book
                                    </Button>
                                )}
                            </Empty>
                        ),
                    }}
                />
            </CustomCard>

            <OtherBookForm
                visible={formVisible}
                onClose={() => setFormVisible(false)}
                book={editing}
                sites={sites}
                categories={categories}
                onSaved={() => { fetchBooks(pagination.current, query); loadStats(); loadCategories(); }}
            />
        </div>
    );
};

export default OtherBooks;
